import type { Coin, Transaction } from '@asia/shared';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * 24h portfolio move for the balance-card pill: price P&L on what you held
 * plus net in/out from confirmed transfers. Pure price-backout ignores sends/
 * receives (both sides of the formula scale with the new balance).
 */
export function portfolioChange24h(
  coins: Pick<Coin, 'symbol' | 'balance' | 'priceUsd' | 'change24h'>[],
  txs: Pick<Transaction, 'coin' | 'amount' | 'date' | 'status'>[],
  now = Date.now(),
): { changeUsd: number; changePct: number } {
  const since = now - DAY_MS;
  const flow: Partial<Record<string, number>> = {};
  for (const tx of txs) {
    if (tx.status !== 'confirmed') continue;
    if (Date.parse(tx.date) < since) continue;
    flow[tx.coin] = (flow[tx.coin] ?? 0) + tx.amount;
  }

  const total = coins.reduce((s, c) => s + c.balance * c.priceUsd, 0);
  const prevTotal = coins.reduce((s, c) => {
    const balanceAgo = c.balance - (flow[c.symbol] ?? 0);
    const denom = 1 + c.change24h;
    const priceAgo = denom !== 0 ? c.priceUsd / denom : c.priceUsd;
    return s + balanceAgo * priceAgo;
  }, 0);

  const changeUsd = total - prevTotal;
  const changePct = prevTotal > 0 ? changeUsd / prevTotal : total > 0 ? 1 : 0;
  return { changeUsd, changePct };
}
